import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  completeBankReconciliation,
  createBankReconciliation,
  fetchBankReconciliations,
  fetchChartOfAccounts,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const BankReconciliationPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    cash_account_id: "",
    statement_date: todayIsoDate(),
    statement_balance: "",
    notes: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(
      () =>
        Promise.all([
          fetchBankReconciliations(branchId).then(setRows),
          fetchChartOfAccounts({ branch_id: branchId, account_type: "asset", active_only: true }).then(setAccounts),
        ]),
      { errorMessage: "Failed to load reconciliations." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load reconciliations.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const loadRef = useLatestRef(load);

  const completeRecon = useCallback(
    (row) => {
      confirm({
        title: "Complete reconciliation?",
        message: "Mark this reconciliation as completed.",
        confirmLabel: "Complete",
        successMessage: "Reconciliation completed.",
        onConfirm: async () => {
          await completeBankReconciliation(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Date", cell: (r) => formatDate(r.statement_date), width: "110px" },
      { name: "Cash account", selector: (r) => r.cash_account_name, grow: 2 },
      { name: "Statement", cell: (r) => formatTZS(r.statement_balance), right: true, width: "130px" },
      { name: "Ledger", cell: (r) => formatTZS(r.ledger_balance), right: true, width: "130px" },
      { name: "Diff", cell: (r) => formatTZS(r.difference), right: true, width: "120px" },
      {
        name: "Status",
        cell: (r) => (
          <Badge color={r.status === "completed" ? "success" : "warning"}>
            {r.status === "completed" ? "Completed" : "In progress"}
          </Badge>
        ),
        width: "110px",
      },
      {
        name: "",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: "eye",
                label: "View reconciliation",
                onClick: () => setDetail(r),
              },
              r.status === "in_progress" && {
                icon: "check",
                label: "Complete reconciliation",
                onClick: () => completeRecon(r),
                variant: "primary",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [completeRecon]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await runCrud(
        () => createBankReconciliation({
          branch_id: branchId,
          cash_account_id: form.cash_account_id,
          statement_date: form.statement_date,
          statement_balance: Number(form.statement_balance),
          notes: form.notes || null,
        }),
        { successMessage: "Reconciliation started." }
      );
      setModalOpen(false);
      setDetail(created);
      load();
    } finally {
      setSaving(false);
    }
  };

  const itemColumns = [
    { name: "Description", selector: (r) => r.description, grow: 2 },
    { name: "Amount", cell: (r) => formatTZS(r.amount), right: true, width: "130px" },
    { name: "Matched", cell: (r) => (r.is_matched ? "Yes" : "No"), width: "80px" },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Bank Reconciliation" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Reconciliation sessions</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New reconciliation</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        {detail && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Items — {detail.cash_account_name}</h6>
              <Button size="sm" color="light" className="border" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div className="card-body p-0">
              <DataTable columns={itemColumns} data={detail.items || []} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination dense />
            </div>
          </div>
        )}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>Start bank reconciliation</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Cash / bank GL account</Label>
                <Input type="select" required value={form.cash_account_id} onChange={(e) => setForm({ ...form, cash_account_id: e.target.value })}>
                  <option value="">Select account</option>
                  {accounts.filter((a) => a.code.startsWith("100")).map((a) => (
                    <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                  ))}
                </Input>
              </FormGroup>
              <Row>
                <Col md="6"><FormGroup><Label>Statement date</Label>
                  <Input type="date" required value={form.statement_date} onChange={(e) => setForm({ ...form, statement_date: e.target.value })} /></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Statement balance</Label>
                  <Input type="number" step="0.01" required value={form.statement_balance} onChange={(e) => setForm({ ...form, statement_balance: e.target.value })} /></FormGroup></Col>
              </Row>
              <FormGroup><Label>Notes</Label><Input type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>Start</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default BankReconciliationPage;
